import { memo } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIAlert from 'UIComponents/alert/UIAlert';
import { SLINKY, GYPSUM, KOALA } from 'HubStyleTokens/colors';
import SkeletonText from 'conversations-skeleton-state/components/SkeletonText';
import QuarantinedMessageAlert from '../../../common-message/QuarantinedMessageAlert';
import { truncateString } from '../../../util/truncation/truncateString';
import MessagePreviewAttachments from './MessagePreviewAttachments';
import { getLocalDeepLinkUrlFromPathName } from '../../../url/getDeepLinkUrl';
import { useHistory, useLocation } from 'react-router-dom';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MessageReplyWrapper = styled.div.withConfig({
  displayName: "MessagePreview__MessageReplyWrapper",
  componentId: "sc-4obihx-0"
})(["padding:8px;background-color:", ";border-radius:4px;border-left:none;cursor:default;"], KOALA);
const NoStyleATag = styled.a.withConfig({
  displayName: "MessagePreview__NoStyleATag",
  componentId: "sc-4obihx-1"
})(["font-weight:normal;-webkit-font-smoothing:auto;&:hover{text-decoration:none;}"]);
const FlexedMessageReplyWrapper = styled(MessageReplyWrapper).withConfig({
  displayName: "MessagePreview__FlexedMessageReplyWrapper",
  componentId: "sc-4obihx-2"
})(["display:flex;justify-content:space-between;border-left:none;&:hover{background-color:", ";cursor:pointer;}"], GYPSUM);
const StyledBody = styled.div.withConfig({
  displayName: "MessagePreview__StyledBody",
  componentId: "sc-4obihx-3"
})(["font-size:12px;line-height:normal;color:", ";"], SLINKY);
const BoldedBody = styled(StyledBody).withConfig({
  displayName: "MessagePreview__BoldedBody",
  componentId: "sc-4obihx-4"
})(["margin-bottom:5px;font-weight:500;"]);
const AttachmentIconWrapper = styled.span.withConfig({
  displayName: "MessagePreview__AttachmentIconWrapper",
  componentId: "sc-4obihx-5"
})(["margin-right:5px;"]);
const ItalicSpan = styled.span.withConfig({
  displayName: "MessagePreview__ItalicSpan",
  componentId: "sc-4obihx-6"
})(["font-style:italic;"]);
const TextWrapper = styled.div.withConfig({
  displayName: "MessagePreview__TextWrapper",
  componentId: "sc-4obihx-7"
})(["flex-grow:1;overflow:hidden;"]);
const SkeletonTextSpacingWrapper = styled.div.withConfig({
  displayName: "MessagePreview__SkeletonTextSpacingWrapper",
  componentId: "sc-4obihx-8"
})(["margin-bottom:5px;"]);
const MessagePreview = props => {
  const {
    isLoading,
    isPreviewable
  } = props;
  const history = useHistory();
  const location = useLocation();
  if (isLoading) {
    return /*#__PURE__*/_jsxs(MessageReplyWrapper, {
      children: [/*#__PURE__*/_jsx(SkeletonTextSpacingWrapper, {
        children: /*#__PURE__*/_jsx(SkeletonText, {
          "data-test-id": "skeleton-text",
          width: 60,
          height: 15
        })
      }), /*#__PURE__*/_jsx(SkeletonText, {
        "data-test-id": "skeleton-text",
        width: 150,
        height: 15
      })]
    });
  }
  if (!isPreviewable) {
    return /*#__PURE__*/_jsx(MessageReplyWrapper, {
      children: /*#__PURE__*/_jsx(UIAlert, {
        type: "warning",
        style: {
          minHeight: '35px'
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-inbox-message-history.message-preview.unavailable"
        })
      })
    });
  }
  const {
    attachmentPreview,
    isQuarantined,
    maxPreviewLength,
    messageText,
    senderName,
    messageId
  } = props;
  if (isQuarantined) {
    return /*#__PURE__*/_jsxs(MessageReplyWrapper, {
      children: [/*#__PURE__*/_jsx(BoldedBody, {
        children: senderName
      }), /*#__PURE__*/_jsx(QuarantinedMessageAlert, {
        shouldRenderPreviewAlert: true
      })]
    });
  }
  const {
    hasMissingAttachment,
    icon: attachmentIcon,
    name: attachmentName,
    type: attachmentType
  } = attachmentPreview;
  const previewText = messageText && maxPreviewLength ? truncateString(messageText, maxPreviewLength) : messageText || attachmentName || attachmentType;
  return /*#__PURE__*/_jsx(NoStyleATag, {
    href: getLocalDeepLinkUrlFromPathName(location, messageId),
    onClick: evt => {
      //stop navigation and prevent other onClick functions
      evt.preventDefault();
      evt.stopPropagation();
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('messageId', messageId);
      history.replace({
        search: searchParams.toString()
      });
    },
    "data-test-id": 'message-preview-link',
    children: /*#__PURE__*/_jsxs(FlexedMessageReplyWrapper, {
      children: [/*#__PURE__*/_jsxs(TextWrapper, {
        children: [/*#__PURE__*/_jsx(BoldedBody, {
          children: senderName
        }), /*#__PURE__*/_jsxs(StyledBody, {
          children: [attachmentIcon && /*#__PURE__*/_jsx(AttachmentIconWrapper, {
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: attachmentIcon
            })
          }), previewText ? /*#__PURE__*/_jsx("span", {
            children: previewText
          }) : hasMissingAttachment && /*#__PURE__*/_jsx(ItalicSpan, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "conversations-inbox-message-history.attachments.unavailableAttachment"
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(MessagePreview.Attachments, Object.assign({}, attachmentPreview))]
    })
  });
};
MessagePreview.Attachments = MessagePreviewAttachments;
MessagePreview.displayName = 'MessagePreview';
export default /*#__PURE__*/memo(MessagePreview);